import type { RouteRecordRaw } from "vue-router"
import { markRaw } from "vue"
import { injectRouteList } from "@/hooks/useRouter"
import LevelTwoRouter from "@/layout/components/NavigationBar/LevelTwoRouter.vue"
import { StockMangeCreateDetailRoute, StockMangeCreateRoute, StockMangeRoute } from "@/router/routes/RouteNameEnum"
import { type PermissionRequirement, STOCK_ACTION_PERMISSIONS } from "@/utils/roleFeatureAccess"
import notAccessImage from '/empty/stock_empty.jpg?url'

const Layout = () => import("@/layout/index.vue")
const StockDashboard = () => import("@/views/page/stock/Dashboard.vue")
const StockDashboardV2 = () => import("@/views/page/stock/DashboardV2.vue")
const StockManageCommon = () => import("@/views/page/stock/StockManage/StockManageCommon/StockManageCommon.vue")
const StockRealtimeBoard = () => import("@/views/page/stock/StockDashboard/RealtimeBoard.vue")
const StockInventoryDetails = () => import("@/views/page/stock/StockDashboard/InventoryDetails.vue")
const StockInOutDetail = () => import("@/views/page/dashboard/components/StockStatement/StockDetail.vue")
const StockInOutSummary = () => import("@/views/page/dashboard/components/StockStatement/StockStat.vue")
const StockProfitLoss = () => import("@/views/page/stock/StockDashboard/CheckProfitLoss.vue")
const StockConsumptionOrder = () => import("@/views/page/stock/StockDashboard/ConsumptionOrder.vue")
const StockInventoryLedgerDetail = () => import("@/views/page/stock/StockDashboard/InventoryLedgerDetail.vue")
const StockInventoryChangeProcessSummary = () =>
  import("@/views/page/stock/StockDashboard/InventoryChangeProcessSummary.vue")
const StockManageCommonDetail = () =>
  import("@/views/page/stock/StockManage/StockManageCommon/StockManageCommonDetail.vue")
const StockManageCreate = () => import("@/views/page/stock/StockManage/StockManageCommon/StockManageCreate.vue")
const ObjectManage = () => import("@/views/page/stock/StockSetting/ObjectManage.vue")
const AddOrEditObject = () => import("@/views/page/stock/StockSetting/AddOrEditObject.vue")
const CostCardManage = () => import("@/views/page/stock/StockSetting/CostCardManage.vue")
const StockImport = () => import("@/views/page/stock/StockSetting/StockImport.vue")

const toPermissionArray = (permissions: PermissionRequirement) => {
  return Array.isArray(permissions) ? permissions : [permissions]
}

const STOCK_MANAGE_GROUP_PERMISSIONS = [
  ...toPermissionArray(STOCK_ACTION_PERMISSIONS.viewStock),
  ...toPermissionArray(STOCK_ACTION_PERMISSIONS.stockIn),
  ...toPermissionArray(STOCK_ACTION_PERMISSIONS.stockOut),
  ...toPermissionArray(STOCK_ACTION_PERMISSIONS.stockCheck),
  ...toPermissionArray(STOCK_ACTION_PERMISSIONS.stockAdjust),
]

const STOCK_DASHBOARD_GROUP_PERMISSIONS = [
  ...toPermissionArray(STOCK_ACTION_PERMISSIONS.stockReports),
  ...toPermissionArray(STOCK_ACTION_PERMISSIONS.stockConsume),
]

const STOCK_SETTING_GROUP_PERMISSIONS = [
  "setting_shop_manage",
  ...toPermissionArray(STOCK_ACTION_PERMISSIONS.manageItems),
  ...toPermissionArray(STOCK_ACTION_PERMISSIONS.manageCostCards),
]

const StockManageRouter: RouteRecordRaw[] = [
  {
    path: "/stock/stock-manage/stock-stat",
    redirect: "/stock/stock-manage/stock-stat/index",
    component: markRaw(LevelTwoRouter),
    meta: {
      title: "库存工作台",
      permissions: ["stock_info"],
      keepAlive: true,
      hidden: true,
      notAccessImage,
    },
    children: [
      {
        path: "/stock/stock-manage/stock-stat/index",
        component: StockDashboardV2,
        name: StockMangeRoute.StockStat,
        meta: {
          title: "库存工作台",
          permissions: ["stock_info"],
          hidden: true,
          notAccessImage,
        },
      },
    ],
  },
]

const StockOutRouter: RouteRecordRaw[] = [
  {
    path: "/stock/stock-manage/stock-out",
    redirect: "/stock/stock-manage/stock-out/index",
    component: markRaw(LevelTwoRouter),
    meta: {
      title: "物品出库",
      permissions: ["stock_out"],
      keepAlive: true,
      hidden: true,
      notAccessImage,
    },
    children: [
      {
        path: "/stock/stock-manage/stock-out/index",
        component: StockManageCommon,
        name: StockMangeRoute.StockOut,
        meta: {
          title: "物品出库",
          permissions: ["stock_out"],
          hidden: true,
          notAccessImage,
        },
      },
      {
        path: "/stock/stock-manage/stock-out/create",
        component: StockManageCreate,
        name: StockMangeCreateRoute.StockOut,
        meta: {
          title: "新建出库",
          permissions: ["stock_out"],
          hidden: true,
          notAccessImage,
        },
      },
      {
        path: "/stock/stock-manage/stock-out/detail",
        component: StockManageCommonDetail,
        name: StockMangeCreateDetailRoute.StockOut,
        meta: {
          title: "出库单详情",
          permissions: ["stock_out"],
          hidden: true,
          notAccessImage,
        },
      },
    ],
  },
]

const StockInRouter: RouteRecordRaw[] = [
  {
    path: "/stock/stock-manage/stock-in",
    redirect: "/stock/stock-manage/stock-in/index",
    component: markRaw(LevelTwoRouter),
    meta: {
      title: "物品入库",
      permissions: ["stock_in"],
      keepAlive: true,
      hidden: true,
      notAccessImage,
    },
    children: [
      {
        path: "/stock/stock-manage/stock-in/index",
        component: StockManageCommon,
        name: StockMangeRoute.StockIn,
        meta: {
          title: "物品入库",
          permissions: ["stock_in"],
          hidden: true,
          notAccessImage,
        },
      },
      {
        path: "/stock/stock-manage/stock-in/create",
        component: StockManageCreate,
        name: StockMangeCreateRoute.StockIn,
        meta: {
          title: "新建入库",
          permissions: ["stock_in"],
          hidden: true,
          notAccessImage,
        },
      },
      {
        path: "/stock/stock-manage/stock-in/detail",
        component: StockManageCommonDetail,
        name: StockMangeCreateDetailRoute.StockIn,
        meta: {
          title: "入库单详情",
          permissions: ["stock_in"],
          hidden: true,
          notAccessImage,
        },
      },
    ],
  },
]

const CheckRecordRouter: RouteRecordRaw[] = [
  {
    path: "/stock/stock-manage/check-record",
    redirect: "/stock/stock-manage/check-record/index",
    component: markRaw(LevelTwoRouter),
    meta: {
      title: "盘点单",
      permissions: ["stock_add_inventory"],
      keepAlive: true,
      hidden: true,
      notAccessImage,
    },
    children: [
      {
        path: "/stock/stock-manage/check-record/index",
        component: StockManageCommon,
        name: StockMangeRoute.CheckRecord,
        meta: {
          title: "盘点单",
          permissions: ["stock_add_inventory"],
          hidden: true,
          notAccessImage,
        },
      },
      {
        path: "/stock/stock-manage/check-record/create",
        component: StockManageCreate,
        name: StockMangeCreateRoute.CheckRecord,
        meta: {
          title: "新建盘点",
          permissions: ["stock_add_inventory"],
          hidden: true,
          notAccessImage,
        },
      },
      {
        path: "/stock/stock-manage/check-record/detail",
        component: StockManageCommonDetail,
        name: StockMangeCreateDetailRoute.CheckRecord,
        props: true,
        meta: {
          title: "盘点单详情",
          permissions: ["stock_add_inventory"],
          hidden: true,
          notAccessImage,
        },
      },
    ],
  },
]

const StockAdjustmentRouter: RouteRecordRaw[] = [
  {
    path: "/stock/stock-manage/stock-adjustment",
    redirect: "/stock/stock-manage/stock-adjustment/index",
    component: markRaw(LevelTwoRouter),
    meta: {
      title: "期末成本调整",
      permissions: ["stock_adjustment"],
      keepAlive: true,
      hidden: true,
      notAccessImage,
    },
    children: [
      {
        path: "/stock/stock-manage/stock-adjustment/index",
        component: StockManageCommon,
        name: StockMangeRoute.StockAdjustment,
        meta: {
          title: "期末成本调整",
          permissions: ["stock_adjustment"],
          hidden: true,
          notAccessImage,
        },
      },
      {
        path: "/stock/stock-manage/stock-adjustment/create",
        component: StockManageCreate,
        name: StockMangeCreateRoute.StockAdjustment,
        meta: {
          title: "新建期末成本调整",
          permissions: ["stock_adjustment"],
          hidden: true,
          notAccessImage,
        },
      },
      {
        path: "/stock/stock-manage/stock-adjustment/detail",
        component: StockManageCommonDetail,
        name: StockMangeCreateDetailRoute.StockAdjustment,
        props: true,
        meta: {
          title: "期末成本调整详情",
          permissions: ["stock_adjustment"],
          hidden: true,
          notAccessImage,
        },
      },
    ],
  },
]

export const StockManageChildrenRouter: RouteRecordRaw[] = [
  ...StockManageRouter,
  ...StockInRouter,
  ...StockOutRouter,
  ...CheckRecordRouter,
  ...StockAdjustmentRouter,
]

export const StockDashboardChildrenRouter: RouteRecordRaw[] = [
  {
    path: "/stock/stock-dashboard/realtime-board",
    redirect: "/stock/stock-dashboard/realtime-board/index",
    component: markRaw(LevelTwoRouter),
    meta: {
      title: "实时库存看板",
      permissions: ["dashboard_stock"],
      hidden: true,
      notAccessImage,
    },
    children: [
      {
        path: "/stock/stock-dashboard/realtime-board/index",
        component: StockRealtimeBoard,
        name: "StockRealtimeBoard",
        meta: {
          title: "实时库存看板",
          permissions: ["dashboard_stock"],
          hidden: true,
          notAccessImage,
        },
      },
    ],
  },
  {
    path: "/stock/stock-dashboard/inventory-details",
    redirect: "/stock/stock-dashboard/inventory-details/index",
    component: markRaw(LevelTwoRouter),
    meta: {
      title: "库存明细表",
      permissions: ["dashboard_stock"],
      hidden: true,
      notAccessImage,
    },
    children: [
      {
        path: "/stock/stock-dashboard/inventory-details/index",
        component: StockInventoryDetails,
        name: "StockInventoryDetails",
        meta: {
          title: "库存明细表",
          permissions: ["dashboard_stock"],
          hidden: true,
          notAccessImage,
        },
      },
    ],
  },
  {
    path: "/stock/stock-dashboard/inout-detail",
    redirect: "/stock/stock-dashboard/inout-detail/index",
    component: markRaw(LevelTwoRouter),
    meta: {
      title: "出入库明细表",
      permissions: ["dashboard_stock"],
      hidden: true,
      notAccessImage,
    },
    children: [
      {
        path: "/stock/stock-dashboard/inout-detail/index",
        component: StockInOutDetail,
        name: "StockDetail",
        meta: {
          title: "出入库明细表",
          permissions: ["dashboard_stock"],
          hidden: true,
          notAccessImage,
        },
      },
    ],
  },
  {
    path: "/stock/stock-dashboard/inout-summary",
    redirect: "/stock/stock-dashboard/inout-summary/index",
    component: markRaw(LevelTwoRouter),
    meta: {
      title: "出入库汇总表",
      permissions: ["dashboard_stock"],
      hidden: true,
      notAccessImage,
    },
    children: [
      {
        path: "/stock/stock-dashboard/inout-summary/index",
        component: StockInOutSummary,
        name: "StockStat",
        meta: {
          title: "出入库汇总表",
          permissions: ["dashboard_stock"],
          hidden: true,
          notAccessImage,
        },
      },
    ],
  },
  {
    path: "/stock/stock-dashboard/check-profit-loss",
    redirect: "/stock/stock-dashboard/check-profit-loss/index",
    component: markRaw(LevelTwoRouter),
    meta: {
      title: "盘点盈亏表",
      permissions: ["dashboard_stock"],
      hidden: true,
      notAccessImage,
    },
    children: [
      {
        path: "/stock/stock-dashboard/check-profit-loss/index",
        component: StockProfitLoss,
        name: "StockCheckProfitLoss",
        meta: {
          title: "盘点盈亏表",
          permissions: ["dashboard_stock"],
          hidden: true,
          notAccessImage,
        },
      },
    ],
  },
  {
    path: "/stock/stock-dashboard/consumption-table",
    redirect: "/stock/stock-dashboard/consumption-table/index",
    component: markRaw(LevelTwoRouter),
    meta: {
      title: "物品消耗表",
      permissions: ["stock_sale"],
      hidden: true,
      notAccessImage,
    },
    children: [
      {
        path: "/stock/stock-dashboard/consumption-table/index",
        component: StockConsumptionOrder,
        name: StockMangeRoute.ConsumptionOrder,
        meta: {
          title: "物品消耗表",
          permissions: ["stock_sale"],
          hidden: true,
          notAccessImage,
        },
      },
      {
        path: "/stock/stock-dashboard/consumption-table/old_index",
        component: StockManageCommon,
        name: StockMangeRoute.ConsumptionOrderOld,
        meta: {
          title: "物品消耗表(旧版)",
          permissions: ["stock_sale"],
          hidden: true,
          notAccessImage,
        },
      },
      {
        path: "/stock/stock-dashboard/consumption-table/detail",
        component: StockManageCommonDetail,
        name: StockMangeCreateDetailRoute.ConsumptionOrder,
        meta: {
          title: "消耗表详情",
          permissions: ["stock_sale"],
          hidden: true,
          notAccessImage,
        },
      },
    ],
  },
  {
    path: "/stock/stock-dashboard/inventory-ledger-detail",
    redirect: "/stock/stock-dashboard/inventory-ledger-detail/index",
    component: markRaw(LevelTwoRouter),
    meta: {
      title: "进销存明细表",
      permissions: ["dashboard_stock"],
      hidden: true,
      notAccessImage,
    },
    children: [
      {
        path: "/stock/stock-dashboard/inventory-ledger-detail/index",
        component: StockInventoryLedgerDetail,
        name: "StockInventoryLedgerDetail",
        meta: {
          title: "进销存明细表",
          permissions: ["dashboard_stock"],
          hidden: true,
          notAccessImage,
        },
      },
    ],
  },
  {
    path: "/stock/stock-dashboard/inventory-change-process-summary",
    redirect: "/stock/stock-dashboard/inventory-change-process-summary/index",
    component: markRaw(LevelTwoRouter),
    meta: {
      title: "出入库日报",
      permissions: ["dashboard_stock"],
      hidden: true,
      notAccessImage,
    },
    children: [
      {
        path: "/stock/stock-dashboard/inventory-change-process-summary/index",
        component: StockInventoryChangeProcessSummary,
        name: "StockInventoryChangeProcessSummary",
        meta: {
          title: "出入库日报",
          permissions: ["dashboard_stock"],
          hidden: true,
          notAccessImage,
        },
      },
    ],
  },
]

const StockSettingStockRouter: RouteRecordRaw[] = [
  {
    path: "/stock/stock-setting/stock/index",
    component: ObjectManage,
    name: "ObjectManage",
    meta: {
      title: "物品管理",
      permissions: ["stock_object"],
      hidden: true,
      notAccessImage,
    },
  },
  {
    path: "/stock/stock-setting/stock/create",
    component: AddOrEditObject,
    name: "AddObject",
    meta: {
      title: "新增物品",
      permissions: ["stock_object"],
      hidden: true,
      notAccessImage,
    },
  },
  {
    path: "/stock/stock-setting/stock/edit",
    component: AddOrEditObject,
    name: "EditObject",
    meta: {
      title: "编辑物品",
      permissions: ["stock_object"],
      hidden: true,
      notAccessImage,
    },
  },
  {
    path: "/stock/stock-setting/stock/import",
    component: StockImport,
    name: "StockSettingStockImport",
    meta: {
      title: "商品导入",
      permissions: ["stock_object"],
      hidden: true,
      notAccessImage,
    },
  },
]

export const StockSettingChildrenRouter: RouteRecordRaw[] = [
  {
    path: "/stock/stock-setting/count",
    name: "InventoryCountSettings",
    component: () => import("@/views/page/stock/InventoryCountSettings.vue"),
    meta: { title: "持续盘点设置", permissions: ["setting_shop_manage"], notAccessImage },
  },
  {
    path: "/stock/stock-setting/stock",
    component: LevelTwoRouter,
    redirect: "/stock/stock-setting/stock/index",
    meta: {
      title: "物品管理",
      permissions: ["stock_object"],
      hidden: true,
      notAccessImage,
    },
    children: StockSettingStockRouter,
  },
  {
    path: "/stock/stock-setting/cost-card",
    component: LevelTwoRouter,
    redirect: "/stock/stock-setting/cost-card/index",
    meta: {
      title: "成本卡管理",
      permissions: ["stock_cast_card"],
      hidden: true,
      notAccessImage,
    },
    children: [
      {
        path: "/stock/stock-setting/cost-card/index",
        component: CostCardManage,
        name: "CostCardManage",
        meta: {
          title: "成本卡管理",
          permissions: ["stock_cast_card"],
          hidden: true,
          notAccessImage,
        },
      },
    ],
  },
]

export const firstStockLevelRoute: RouteRecordRaw[] = [
  {
    path: "/stock/stock-stat",
    redirect: "/stock/stock-stat/index",
    meta: {
      title: "库存概览",
      permissions: ["stock_info"],
      notAccessImage,
      hidden: true,
    },
    children: [
      {
        path: "/stock/stock-stat/index",
        component: StockDashboard,
        name: "StockDashboard",
        meta: {
          title: "库存概览",
          permissions: ["stock_info"],
          notAccessImage,
        },
      },
    ],
  },
  {
    path: "/stock/stock-manage",
    redirect: "/stock/stock-manage/stock-stat",
    meta: {
      title: "库存管理",
      permissions: STOCK_MANAGE_GROUP_PERMISSIONS,
      notAccessImage,
      activeMenu: '/stock/stock-manage',
    },
    children: injectRouteList(StockManageChildrenRouter),
  },
  {
    path: "/stock/stock-dashboard",
    redirect: "/stock/stock-dashboard/realtime-board",
    meta: {
      title: "库存报表",
      permissions: STOCK_DASHBOARD_GROUP_PERMISSIONS,
      notAccessImage,
      activeMenu: '/stock/stock-dashboard',
    },
    children: injectRouteList(StockDashboardChildrenRouter),
  },
  {
    path: "/stock/stock-setting",
    redirect: "/stock/stock-setting/stock",
    meta: {
      title: "基础设置",
      permissions: STOCK_SETTING_GROUP_PERMISSIONS,
      notAccessImage,
      activeMenu: '/stock/stock-setting',
    },
    children: injectRouteList(StockSettingChildrenRouter),
  },
]

export const stock: RouteRecordRaw[] = [
  {
    path: "/stock",
    component: Layout,
    redirect: "/stock/stock-manage/stock-stat",
    children: injectRouteList(firstStockLevelRoute),
    meta: {
      title: "库存",
      svgIcon: "nav-stock",
      order: 4,
      permissions: ["stock"],
      notAccessImage,
    },
  },
]
export default stock
